import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Megaphone, Briefcase, Loader2, Pencil, Trash2, Plus, Users, Crown, Upload } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '';
const AD_TYPES = ['image', 'video'];

const emptyAdForm = { title: '', description: '', adType: 'image', mediaUrl: '', mediaUrlVertical: '', clickUrl: '', duration: 5, weight: 1, campaignId: '', isActive: true };
const emptyCampaignForm = { name: '', adType: 'image', targetUrl: '', revenuePerClick: 0, revenuePerImpression: 0, budget: '', startDate: '', endDate: '', isActive: true };

/*
 * AdminAds — beheer van ads en campagnes.
 * Praat met de /api/admin/ads* en /api/admin/campaigns* endpoints in server.js. Alleen voor admins.
 */
export default function AdminAds({ onClose, token }) {
  const [tab, setTab] = useState('ads'); // ads | campaigns | users
  const [ads, setAds] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');
  const [adForm, setAdForm] = useState(null);   // null = closed, object = editing/creating
  const [campaignForm, setCampaignForm] = useState(null);
  const [uploadingField, setUploadingField] = useState(null); // 'mediaUrl' | 'mediaUrlVertical' | null
  const landscapeFileRef = useRef(null);
  const verticalFileRef = useRef(null);

  // Users/plan tab — manual premium grant, ahead of real subscription billing (see CLAUDE.md).
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [planBusyId, setPlanBusyId] = useState(null);

  const authHeaders = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2500); };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [adsRes, campaignsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/ads`, { headers: authHeaders }),
        fetch(`${API_URL}/api/admin/campaigns`, { headers: authHeaders }),
      ]);
      const adsData = await adsRes.json();
      const campaignsData = await campaignsRes.json();
      setAds(adsData.ads || []);
      setCampaigns(campaignsData.campaigns || []);
    } catch (e) {
      showToast('Laden mislukt');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // --- Users / plan -----------------------------------------------------------
  const loadUsers = useCallback(async (search) => {
    setLoadingUsers(true);
    try {
      const qs = search ? `?search=${encodeURIComponent(search)}` : '';
      const res = await fetch(`${API_URL}/api/admin/users${qs}`, { headers: authHeaders });
      const data = await res.json();
      setUsers(data.users || []);
    } catch (e) {
      showToast('Loading users failed');
    } finally {
      setLoadingUsers(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (tab !== 'users') return;
    const t = setTimeout(() => loadUsers(userSearch), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, userSearch]);

  const setUserPlan = async (user, nextPlan) => {
    setPlanBusyId(user.id);
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${user.id}/plan`, {
        method: 'PUT', headers: authHeaders, body: JSON.stringify({ plan: nextPlan }),
      });
      const data = await res.json();
      if (!res.ok) return showToast(data.error || 'Update failed');
      setUsers((prev) => prev.map((u) => (u.id === user.id ? data.user : u)));
      const label = nextPlan === 'premium' ? 'now premium (ad-free)' : nextPlan === 'premium_requested' ? 'marked as requested' : 'back on free';
      showToast(`${user.username} is ${label}`);
    } catch (e) { showToast('Update failed'); }
    finally { setPlanBusyId(null); }
  };

  // --- Ads ------------------------------------------------------------------
  const saveAd = async () => {
    if (!adForm.title.trim() || !adForm.clickUrl.trim()) return showToast('Title and click URL are required');
    setBusy(true);
    try {
      const body = { ...adForm, campaignId: adForm.campaignId || null };
      const isEdit = !!adForm.id;
      const res = await fetch(`${API_URL}/api/admin/ads${isEdit ? `/${adForm.id}` : ''}`, {
        method: isEdit ? 'PUT' : 'POST', headers: authHeaders, body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) return showToast(data.error || 'Opslaan mislukt');
      showToast(isEdit ? 'Ad updated' : 'Ad created');
      setAdForm(null);
      loadAll();
    } catch (e) { showToast('Opslaan mislukt'); }
    finally { setBusy(false); }
  };

  // Uploads a banner image (JPEG only) or short clip (MP4/WebM) and drops the returned
  // /media/<uuid>.<ext> URL straight into the given form field.
  const uploadMedia = async (file, field) => {
    if (!file) return;
    setUploadingField(field);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API_URL}/api/admin/ads/upload-media`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) return showToast(data.error || 'Upload failed');
      setAdForm((prev) => (prev ? { ...prev, [field]: data.url } : prev));
      showToast('Uploaded');
    } catch (e) { showToast('Upload failed'); }
    finally { setUploadingField(null); }
  };

  const deleteAd = async (ad) => {
    if (!window.confirm(`Delete ad "${ad.title}"?`)) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/ads/${ad.id}`, { method: 'DELETE', headers: authHeaders });
      const data = await res.json();
      if (!res.ok) return showToast(data.error || 'Delete failed');
      showToast('Ad deleted');
      loadAll();
    } catch (e) { showToast('Delete failed'); }
    finally { setBusy(false); }
  };

  // --- Campaigns --------------------------------------------------------------
  const saveCampaign = async () => {
    if (!campaignForm.name.trim()) return showToast('Name is required');
    setBusy(true);
    try {
      const isEdit = !!campaignForm.id;
      const body = { ...campaignForm, budget: campaignForm.budget === '' ? null : campaignForm.budget };
      const res = await fetch(`${API_URL}/api/admin/campaigns${isEdit ? `/${campaignForm.id}` : ''}`, {
        method: isEdit ? 'PUT' : 'POST', headers: authHeaders, body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) return showToast(data.error || 'Opslaan mislukt');
      showToast(isEdit ? 'Campaign updated' : 'Campaign created');
      setCampaignForm(null);
      loadAll();
    } catch (e) { showToast('Opslaan mislukt'); }
    finally { setBusy(false); }
  };

  const deleteCampaign = async (c) => {
    if (!window.confirm(`Delete campaign "${c.name}"?`)) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/campaigns/${c.id}`, { method: 'DELETE', headers: authHeaders });
      const data = await res.json();
      if (!res.ok) return showToast(data.error || 'Delete failed');
      showToast('Campaign deleted');
      loadAll();
    } catch (e) { showToast('Delete failed'); }
    finally { setBusy(false); }
  };

  const ctr = (ad) => (ad.views > 0 ? ((ad.clicks / ad.views) * 100).toFixed(2) : '0.00');

  return (
    <div className="fixed inset-0 z-50 bg-zinc-950 text-white flex flex-col">
      {/* Kop */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800 bg-zinc-900">
        <div className="flex items-center gap-2">
          <Megaphone className="w-5 h-5 text-red-500" />
          <h2 className="font-bold text-lg">Ads beheer</h2>
        </div>
        <button onClick={onClose} className="p-2 rounded hover:bg-zinc-800" aria-label="Sluiten">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Tabs */}
      <div className="px-5 py-2 border-b border-zinc-800 flex items-center gap-2">
        <button onClick={() => setTab('ads')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-sm ${tab === 'ads' ? 'bg-red-600' : 'bg-zinc-800 hover:bg-zinc-700'}`}>
          <Megaphone className="w-4 h-4" /> Ads ({ads.length})
        </button>
        <button onClick={() => setTab('campaigns')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-sm ${tab === 'campaigns' ? 'bg-red-600' : 'bg-zinc-800 hover:bg-zinc-700'}`}>
          <Briefcase className="w-4 h-4" /> Campaigns ({campaigns.length})
        </button>
        <button onClick={() => setTab('users')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-sm ${tab === 'users' ? 'bg-red-600' : 'bg-zinc-800 hover:bg-zinc-700'}`}>
          <Users className="w-4 h-4" /> Users
        </button>
        <div className="flex-1" />
        {tab !== 'users' && (
          <button
            onClick={() => tab === 'ads' ? setAdForm({ ...emptyAdForm }) : setCampaignForm({ ...emptyCampaignForm })}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 rounded text-sm">
            <Plus className="w-4 h-4" /> New {tab === 'ads' ? 'ad' : 'campaign'}
          </button>
        )}
      </div>
      {tab === 'users' && (
        <div className="px-5 py-2 border-b border-zinc-800">
          <input
            value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)}
            placeholder="Search by username or email…"
            className="w-full max-w-sm bg-zinc-800 rounded px-3 py-1.5 text-sm outline-none focus:ring-1 focus:ring-red-500"
          />
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {tab === 'users' ? (
          loadingUsers ? (
            <div className="flex items-center justify-center h-40 text-zinc-500">
              <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-zinc-500 border-b border-zinc-800">
                  <th className="py-2 pr-3">Username</th>
                  <th className="py-2 pr-3">Email</th>
                  <th className="py-2 pr-3">Plan</th>
                  <th className="py-2 pr-3">Joined</th>
                  <th className="py-2 pr-3" />
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-zinc-900 hover:bg-zinc-900/50">
                    <td className="py-2 pr-3">{u.username}{u.is_admin && <span className="ml-1.5 text-[10px] uppercase text-zinc-500">admin</span>}</td>
                    <td className="py-2 pr-3 text-zinc-400">{u.email}</td>
                    <td className="py-2 pr-3">
                      <span className={`flex items-center gap-1 w-fit px-1.5 py-0.5 rounded text-xs ${
                        u.plan === 'premium' ? 'bg-amber-900 text-amber-300'
                        : u.plan === 'premium_requested' ? 'bg-blue-900 text-blue-300'
                        : 'bg-zinc-800 text-zinc-500'}`}>
                        {u.plan === 'premium' && <Crown className="w-3 h-3" />}
                        {u.plan === 'premium_requested' ? 'requested' : u.plan}
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-zinc-500">{new Date(u.created_at).toLocaleDateString()}</td>
                    <td className="py-2 pr-3">
                      <div className="flex items-center gap-1.5">
                        {u.plan === 'premium' && (
                          <button
                            onClick={() => setUserPlan(u, 'free')}
                            disabled={planBusyId === u.id}
                            className="px-2.5 py-1 rounded text-xs bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50">
                            Revoke premium
                          </button>
                        )}
                        {u.plan === 'free' && (
                          <button
                            onClick={() => setUserPlan(u, 'premium')}
                            disabled={planBusyId === u.id}
                            className="px-2.5 py-1 rounded text-xs bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50">
                            Grant premium
                          </button>
                        )}
                        {u.plan === 'premium_requested' && (
                          <>
                            <button
                              onClick={() => setUserPlan(u, 'premium')}
                              disabled={planBusyId === u.id}
                              className="px-2.5 py-1 rounded text-xs bg-red-600 hover:bg-red-700 disabled:opacity-50">
                              Approve
                            </button>
                            <button
                              onClick={() => setUserPlan(u, 'free')}
                              disabled={planBusyId === u.id}
                              className="px-2.5 py-1 rounded text-xs bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50">
                              Decline
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr><td colSpan={5} className="py-6 text-center text-zinc-500">No users found</td></tr>
                )}
              </tbody>
            </table>
          )
        ) : loading ? (
          <div className="flex items-center justify-center h-40 text-zinc-500">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Laden…
          </div>
        ) : tab === 'ads' ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-zinc-500 border-b border-zinc-800">
                <th className="py-2 pr-3">Title</th>
                <th className="py-2 pr-3">Type</th>
                <th className="py-2 pr-3">Campaign</th>
                <th className="py-2 pr-3">Weight</th>
                <th className="py-2 pr-3">Active</th>
                <th className="py-2 pr-3">Views</th>
                <th className="py-2 pr-3">Clicks</th>
                <th className="py-2 pr-3">CTR</th>
                <th className="py-2 pr-3" />
              </tr>
            </thead>
            <tbody>
              {ads.map((ad) => (
                <tr key={ad.id} className="border-b border-zinc-900 hover:bg-zinc-900/50">
                  <td className="py-2 pr-3 max-w-[220px] truncate" title={ad.title}>{ad.title}</td>
                  <td className="py-2 pr-3">{ad.ad_type}</td>
                  <td className="py-2 pr-3 text-zinc-400">{ad.campaign_name || 'House ad'}</td>
                  <td className="py-2 pr-3">{ad.weight}</td>
                  <td className="py-2 pr-3">
                    <span className={`px-1.5 py-0.5 rounded text-xs ${ad.is_active ? 'bg-green-900 text-green-300' : 'bg-zinc-800 text-zinc-500'}`}>
                      {ad.is_active ? 'Active' : 'Paused'}
                    </span>
                  </td>
                  <td className="py-2 pr-3">{ad.views}</td>
                  <td className="py-2 pr-3">{ad.clicks}</td>
                  <td className="py-2 pr-3">{ctr(ad)}%</td>
                  <td className="py-2 pr-3">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setAdForm({
                          id: ad.id, title: ad.title, description: ad.description || '', adType: ad.ad_type,
                          mediaUrl: ad.media_url || '', mediaUrlVertical: ad.media_url_vertical || '', clickUrl: ad.click_url, duration: ad.duration || 5,
                          weight: ad.weight || 1, campaignId: ad.campaign_id || '', isActive: ad.is_active,
                        })}
                        className="p-1.5 rounded hover:bg-zinc-800" aria-label="Edit"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => deleteAd(ad)} disabled={busy}
                        className="p-1.5 rounded hover:bg-zinc-800 text-red-400" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {ads.length === 0 && (
                <tr><td colSpan={9} className="py-6 text-center text-zinc-500">No ads yet</td></tr>
              )}
            </tbody>
          </table>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-zinc-500 border-b border-zinc-800">
                <th className="py-2 pr-3">Name</th>
                <th className="py-2 pr-3">Type</th>
                <th className="py-2 pr-3">Active</th>
                <th className="py-2 pr-3">Budget</th>
                <th className="py-2 pr-3">Spent</th>
                <th className="py-2 pr-3">Start</th>
                <th className="py-2 pr-3">End</th>
                <th className="py-2 pr-3" />
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c) => (
                <tr key={c.id} className="border-b border-zinc-900 hover:bg-zinc-900/50">
                  <td className="py-2 pr-3 max-w-[200px] truncate" title={c.name}>{c.name}</td>
                  <td className="py-2 pr-3">{c.ad_type}</td>
                  <td className="py-2 pr-3">
                    <span className={`px-1.5 py-0.5 rounded text-xs ${c.is_active ? 'bg-green-900 text-green-300' : 'bg-zinc-800 text-zinc-500'}`}>
                      {c.is_active ? 'Active' : 'Paused'}
                    </span>
                  </td>
                  <td className="py-2 pr-3">{c.budget != null ? `$${c.budget}` : '—'}</td>
                  <td className="py-2 pr-3">${c.spent}</td>
                  <td className="py-2 pr-3">{c.start_date ? new Date(c.start_date).toLocaleDateString() : '—'}</td>
                  <td className="py-2 pr-3">{c.end_date ? new Date(c.end_date).toLocaleDateString() : '—'}</td>
                  <td className="py-2 pr-3">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setCampaignForm({
                          id: c.id, name: c.name, adType: c.ad_type, targetUrl: c.target_url || '',
                          revenuePerClick: c.revenue_per_click || 0, revenuePerImpression: c.revenue_per_impression || 0,
                          budget: c.budget ?? '', startDate: c.start_date ? c.start_date.slice(0, 10) : '',
                          endDate: c.end_date ? c.end_date.slice(0, 10) : '', isActive: c.is_active,
                        })}
                        className="p-1.5 rounded hover:bg-zinc-800" aria-label="Edit"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => deleteCampaign(c)} disabled={busy}
                        className="p-1.5 rounded hover:bg-zinc-800 text-red-400" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {campaigns.length === 0 && (
                <tr><td colSpan={8} className="py-6 text-center text-zinc-500">No campaigns yet</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Ad form */}
      {adForm && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg w-full max-w-md p-5 space-y-3">
            <h3 className="font-bold text-lg">{adForm.id ? 'Edit ad' : 'New ad'}</h3>
            <input placeholder="Title" value={adForm.title} onChange={(e) => setAdForm({ ...adForm, title: e.target.value })}
              className="w-full bg-zinc-800 rounded px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-red-500" />
            <textarea placeholder="Description" value={adForm.description} onChange={(e) => setAdForm({ ...adForm, description: e.target.value })}
              className="w-full bg-zinc-800 rounded px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-red-500" rows={2} />
            <div className="flex gap-2">
              <select value={adForm.adType} onChange={(e) => setAdForm({ ...adForm, adType: e.target.value })}
                className="flex-1 bg-zinc-800 rounded px-3 py-2 text-sm outline-none">
                {AD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <select value={adForm.campaignId} onChange={(e) => setAdForm({ ...adForm, campaignId: e.target.value })}
                className="flex-1 bg-zinc-800 rounded px-3 py-2 text-sm outline-none">
                <option value="">House ad (no campaign)</option>
                {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <div className="flex gap-2">
                <input placeholder="Media URL (image/video) — used in the player banner" value={adForm.mediaUrl} onChange={(e) => setAdForm({ ...adForm, mediaUrl: e.target.value })}
                  className="flex-1 bg-zinc-800 rounded px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-red-500" />
                <input ref={landscapeFileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm" className="hidden"
                  onChange={(e) => uploadMedia(e.target.files[0], 'mediaUrl')} />
                <button type="button" onClick={() => landscapeFileRef.current?.click()} disabled={uploadingField === 'mediaUrl'}
                  className="px-3 py-2 rounded bg-zinc-700 hover:bg-zinc-600 disabled:opacity-50" aria-label="Upload">
                  {uploadingField === 'mediaUrl' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-[11px] text-zinc-500 mt-1">Landscape, ~5:3 (e.g. 600×360) — shown small and cropped in the video player. Upload accepts JPEG, PNG (transparent logos), WebP, GIF, MP4 or WebM.</p>
            </div>
            <div>
              <div className="flex gap-2">
                <input placeholder="Vertical media URL (optional) — used in the feed" value={adForm.mediaUrlVertical} onChange={(e) => setAdForm({ ...adForm, mediaUrlVertical: e.target.value })}
                  className="flex-1 bg-zinc-800 rounded px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-red-500" />
                <input ref={verticalFileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm" className="hidden"
                  onChange={(e) => uploadMedia(e.target.files[0], 'mediaUrlVertical')} />
                <button type="button" onClick={() => verticalFileRef.current?.click()} disabled={uploadingField === 'mediaUrlVertical'}
                  className="px-3 py-2 rounded bg-zinc-700 hover:bg-zinc-600 disabled:opacity-50" aria-label="Upload">
                  {uploadingField === 'mediaUrlVertical' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-[11px] text-zinc-500 mt-1">Portrait, ~9:16 — shown full-screen in the feed. Falls back to the media URL above if left blank.</p>
            </div>
            <input placeholder="Click-through URL" value={adForm.clickUrl} onChange={(e) => setAdForm({ ...adForm, clickUrl: e.target.value })}
              className="w-full bg-zinc-800 rounded px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-red-500" />
            <div className="flex gap-2">
              <input type="number" min={1} placeholder="Duration (s)" value={adForm.duration}
                onChange={(e) => setAdForm({ ...adForm, duration: Number(e.target.value) })}
                className="flex-1 bg-zinc-800 rounded px-3 py-2 text-sm outline-none" />
              <input type="number" min={1} placeholder="Weight" value={adForm.weight}
                onChange={(e) => setAdForm({ ...adForm, weight: Number(e.target.value) })}
                className="flex-1 bg-zinc-800 rounded px-3 py-2 text-sm outline-none" />
            </div>
            {adForm.id && (
              <label className="flex items-center gap-2 text-sm text-zinc-300">
                <input type="checkbox" checked={adForm.isActive} onChange={(e) => setAdForm({ ...adForm, isActive: e.target.checked })} />
                Active
              </label>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setAdForm(null)} className="px-3 py-1.5 rounded text-sm bg-zinc-800 hover:bg-zinc-700">Cancel</button>
              <button onClick={saveAd} disabled={busy} className="px-3 py-1.5 rounded text-sm bg-red-600 hover:bg-red-700 disabled:opacity-50">
                {busy ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Campaign form */}
      {campaignForm && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg w-full max-w-md p-5 space-y-3">
            <h3 className="font-bold text-lg">{campaignForm.id ? 'Edit campaign' : 'New campaign'}</h3>
            <input placeholder="Name" value={campaignForm.name} onChange={(e) => setCampaignForm({ ...campaignForm, name: e.target.value })}
              className="w-full bg-zinc-800 rounded px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-red-500" />
            <div className="flex gap-2">
              <select value={campaignForm.adType} onChange={(e) => setCampaignForm({ ...campaignForm, adType: e.target.value })}
                className="flex-1 bg-zinc-800 rounded px-3 py-2 text-sm outline-none">
                {AD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <input placeholder="Target URL" value={campaignForm.targetUrl} onChange={(e) => setCampaignForm({ ...campaignForm, targetUrl: e.target.value })}
                className="flex-1 bg-zinc-800 rounded px-3 py-2 text-sm outline-none" />
            </div>
            <div className="flex gap-2">
              <input type="number" step="0.0001" min={0} placeholder="Revenue / click" value={campaignForm.revenuePerClick}
                onChange={(e) => setCampaignForm({ ...campaignForm, revenuePerClick: Number(e.target.value) })}
                className="flex-1 bg-zinc-800 rounded px-3 py-2 text-sm outline-none" />
              <input type="number" step="0.0001" min={0} placeholder="Revenue / impression" value={campaignForm.revenuePerImpression}
                onChange={(e) => setCampaignForm({ ...campaignForm, revenuePerImpression: Number(e.target.value) })}
                className="flex-1 bg-zinc-800 rounded px-3 py-2 text-sm outline-none" />
            </div>
            <input type="number" step="0.01" min={0} placeholder="Budget (leave empty = unlimited)" value={campaignForm.budget}
              onChange={(e) => setCampaignForm({ ...campaignForm, budget: e.target.value })}
              className="w-full bg-zinc-800 rounded px-3 py-2 text-sm outline-none" />
            <div className="flex gap-2">
              <input type="date" value={campaignForm.startDate} onChange={(e) => setCampaignForm({ ...campaignForm, startDate: e.target.value })}
                className="flex-1 bg-zinc-800 rounded px-3 py-2 text-sm outline-none" />
              <input type="date" value={campaignForm.endDate} onChange={(e) => setCampaignForm({ ...campaignForm, endDate: e.target.value })}
                className="flex-1 bg-zinc-800 rounded px-3 py-2 text-sm outline-none" />
            </div>
            {campaignForm.id && (
              <label className="flex items-center gap-2 text-sm text-zinc-300">
                <input type="checkbox" checked={campaignForm.isActive} onChange={(e) => setCampaignForm({ ...campaignForm, isActive: e.target.checked })} />
                Active
              </label>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setCampaignForm(null)} className="px-3 py-1.5 rounded text-sm bg-zinc-800 hover:bg-zinc-700">Cancel</button>
              <button onClick={saveCampaign} disabled={busy} className="px-3 py-1.5 rounded text-sm bg-red-600 hover:bg-red-700 disabled:opacity-50">
                {busy ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 bg-zinc-800 border border-zinc-700 px-4 py-2 rounded-lg text-sm shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}
